import { onScopeDispose } from "vue";

import { findTypeAheadMatch, isTypeAheadKey } from "#ce/components/SpList/utils/type-ahead";

// How long a typed run stays one query. The APG suggests roughly this; long enough to type a word,
// short enough that a pause starts a fresh search.
const RESET_AFTER_MS = 500;

/**
 * Moves focus to the row a visitor types the start of, the way a native `<select>` does.
 *
 * Typing several characters in quick succession narrows one query rather than restarting, so
 * "apr" reaches Apricot past Apple. Repeating the one buffered character instead steps through the
 * rows sharing it, which is the only way to reach the second of them.
 *
 * @param {object} options
 * @param {() => Array<string|number|null>} options.labels - The navigable rows' labels, in
 *   navigation order.
 * @param {() => number} options.currentIndex
 * @param {(index: number) => void} options.onMatch - Called with the matched navigation index.
 * @returns {{ handleKeyStroke: (event: KeyboardEvent) => void, reset: () => void }}
 */
export function useTypeAhead({ labels, currentIndex, onMatch }) {
  let query = "";
  let resetTimer;

  function reset() {
    clearTimeout(resetTimer);
    query = "";
  }

  function scheduleReset() {
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => (query = ""), RESET_AFTER_MS);
  }

  onScopeDispose(() => clearTimeout(resetTimer));

  function handleKeyStroke(event) {
    if (!isTypeAheadKey(event)) {
      return;
    }

    event.stopPropagation();

    // Lowercased on entry, so a repeat typed in a different case still reads as a repeat.
    const character = event.key.toLowerCase();
    // A repeat of the one buffered character steps to the next row sharing it, and holds the
    // buffer at that character so the next different one extends it rather than the run.
    const repeated = query === character;

    if (!repeated) {
      query += character;
    }

    scheduleReset();

    // A narrowing query has to be able to match the row already focused.
    const from = repeated ? currentIndex() + 1 : Math.max(currentIndex(), 0);

    const match = findTypeAheadMatch(labels(), query, from);

    if (match >= 0) {
      onMatch(match);
    }
  }

  return { handleKeyStroke, reset };
}
